import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import SupplierTabBar from "./supplierTabBar";
import SupplierProductSelector from "../../components/CustomComponents/modal/SupplierProductSelector";

const UpdateSupplierProducts = () => {
  const { sID } = useParams();
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [supplierData, setSupplierData] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  // Fetch supplier details
  const fetchSupplierDetails = useCallback(async () => {
    if (!sID || !compID) {
      console.log("Missing sID or compID:", { sID, compID });
      return;
    }

    try {
      const response = await axiosInstance.get(APIs.GET_SUPPLIER_BY_ID, {
        params: { CompID: compID, S_ID: sID },
      });
      const data = response.data.data || response.data;
      setSupplierData(data);
    } catch (error) {
      console.error("Error fetching supplier details:", error);
      showErrorToast("Failed to load supplier details");
    }
  }, [sID, compID]);

  // Fetch supplier products
  const fetchSupplierProducts = useCallback(async () => {
    if (!sID || !compID) {
      console.log("Missing sID or compID for products:", { sID, compID });
      return;
    }

    try {
      const response = await axiosInstance.get(APIs.GET_SUPPLIER_PRODUCTS, {
        params: { supplierID: parseInt(sID), compID: compID },
      });
      const data = response.data.data || response.data || [];

      // Transform data to match component expectations
      const transformedData = Array.isArray(data) ? data.map(product => {
        console.log("Raw product from API:", product); // Debug log
        return {
          ID: product.ID || product.id, // Handle both uppercase and lowercase
          menuItem_ID: product.menuItem_ID || product.MenuItem_ID,
          menuItem_Name: product.menuItem_Name || product.MenuItem_Name || product.ProductName,
          menuItem_Type: product.menuItem_Type || product.MenuItem_Type,
          menuItem_Image: product.menuItem_Image,
          Supplier_Price: product.supplier_Price || product.Supplier_Price?.toString() || "",
          Is_Preferred: product.is_Preferred || product.Is_Preferred || false,
          Is_Active: product.is_Active || product.Is_Active || true
        };
      }) : [];
      setSupplierProducts(transformedData);
    } catch (error) {
      console.error("Error fetching supplier products:", error);
      showErrorToast("Failed to load supplier products");
      setSupplierProducts([]);
    }
  }, [sID, compID]);

  // Fetch all products for selection
  const fetchAllProducts = useCallback(async () => {
    if (!compID) return;

    try {
      const response = await axiosInstance.get(APIs.GET_ALL_ITEMS_FOR_GRID, {
        params: { CompID: compID, PageNumber: 1, PageSize: 1000 },
      });
      const data = response.data.data || response.data || [];
      setAllProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load products:", error);
      setAllProducts([]);
    }
  }, [compID]);

  // Handle product selection changes
  const handleProductSelectionChange = (selectedProducts) => {
    setSupplierProducts(selectedProducts);
  };

  // Save relations on modal submit - delete old and add new
  const handleSaveRelations = async (newSelectedProducts) => {
    try {
      setLoading(true);

      // Get current supplier products to delete all existing relations
      const currentResponse = await axiosInstance.get(APIs.GET_SUPPLIER_PRODUCTS, {
        params: { supplierID: parseInt(sID), compID: compID },
      });
      const currentProducts = currentResponse.data.data || [];

      // Delete all existing relations for this supplier
      for (const product of currentProducts) {
        await axiosInstance.delete(APIs.DELETE_SUPPLIER_PRODUCT_RELATION, {
          params: { id: product.ID, compID: compID }
        });
      }

      // Add new relations
      for (const product of newSelectedProducts) {
        const payload = {
          CompID: compID,
          SupplierID: parseInt(sID),
          MenuItem_ID: product.menuItem_ID,
          Supplier_Price: parseFloat(product.Supplier_Price || 0),
          Is_Preferred: product.Is_Preferred || false,
          Is_Active: true
        };
        await axiosInstance.post(APIs.POST_ADD_SUPPLIER_PRODUCT_RELATION, payload);
      }

      showSuccessToast("Supplier relations updated successfully");
      fetchSupplierProducts(); // Refresh the list
      return true; // Indicate success
    } catch (error) {
      console.error("Error saving relations:", error);
      showErrorToast("Failed to save supplier relations");
      return false; // Indicate failure
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setInitialLoading(true);
      await Promise.all([
        fetchSupplierDetails(),
        fetchSupplierProducts()
      ]);
      setInitialLoading(false);
    };

    loadData();
  }, [fetchSupplierDetails, fetchSupplierProducts]);

  // Save supplier products
  const handleSaveProducts = async () => {
    if (supplierProducts.length === 0) {
      showErrorToast("No products to save");
      return;
    }

    // Validate that all products have prices
    const invalidProducts = supplierProducts.filter(p => !p.Supplier_Price || p.Supplier_Price.trim() === "");
    if (invalidProducts.length > 0) {
      showErrorToast("Please enter prices for all selected products");
      return;
    }

    try {
      setLoading(true);

      // Get current supplier products to compare
      const currentResponse = await axiosInstance.get(APIs.GET_SUPPLIER_PRODUCTS, {
        params: { supplierID: parseInt(sID), compID: compID },
      });
      const currentProducts = currentResponse.data.data || [];

      // Remove products that are no longer selected
      const productsToRemove = currentProducts.filter(currentProduct =>
        !supplierProducts.some(selectedProduct => selectedProduct.ID === currentProduct.ID)
      );

      for (const product of productsToRemove) {
        await axiosInstance.delete(APIs.DELETE_SUPPLIER_PRODUCT_RELATION, {
          params: { id: product.ID, compID: compID }
        });
      }

      // Add or update products
      for (const product of supplierProducts) {
        if (product.ID) {
          // Update existing product
          const payload = {
            ID: product.ID,
            Supplier_Price: parseFloat(product.Supplier_Price),
            Is_Preferred: product.Is_Preferred || false,
            Is_Active: true
          };
          await axiosInstance.put(APIs.PUT_UPDATE_SUPPLIER_PRODUCT_RELATION, payload);
        } else {
          // Add new product
          const payload = {
            CompID: compID,
            SupplierID: parseInt(sID),
            MenuItem_ID: product.menuItem_ID,
            Supplier_Price: parseFloat(product.Supplier_Price),
            Is_Preferred: product.Is_Preferred || false
          };
          await axiosInstance.post(APIs.POST_ADD_SUPPLIER_PRODUCT_RELATION, payload);
        }
      }

      showSuccessToast("Products saved successfully");
      fetchSupplierProducts(); // Refresh the list
    } catch (error) {
      showErrorToast("Failed to save products");
    } finally {
      setLoading(false);
    }
  };


  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to="/supplier/manageSupplier">Manage Suppliers</Link>
          </li>
          <li className="breadcrumb-item active">Supplier Products</li>
        </ol>
        <h1 className="page-header mb-0">
          {supplierData ? `${supplierData.s_Name} - Products` : "Supplier Products"}
        </h1>
      </div>
        <div className="ms-auto">
          <button
            onClick={handleSaveProducts}
            className="btn btn-rounded px-4 rounded-pill"
            disabled={loading}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`
            }}
          >
            {loading ? "Saving..." : "Save Products"}
          </button>
        </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="container-fluid p-3">
        <Preloader />
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      {loading && <Preloader />}
      {renderBreadcrumb()}
      {sID && <SupplierTabBar supplierId={sID} />}

      {/* Supplier Products Management */}
      <Panel>
        <PanelHeader
          style={{
            backgroundColor: themeColor,
            color: 'white',
            border: 'none',
            borderRadius: '10px 10px 0 0',
            padding: '15px 20px',
            fontWeight: '600',
            fontSize: '16px'
          }}
        >
          <i className="fa fa-box me-2"></i>
          Supplier Products Management
        </PanelHeader>
        <PanelBody
          style={{
            padding: '25px',
            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
            borderRadius: '0 0 10px 10px',
            border: `1px solid ${themeColor}20`
          }}
        >
          <SupplierProductSelector
            selectedProducts={supplierProducts}
            setSelectedProducts={handleProductSelectionChange}
            setLoading={setLoading}
            compID={compID}
            supplierID={parseInt(sID)}
            onSaveRelations={handleSaveRelations}
            onProductUpdated={setSupplierProducts}
          />
        </PanelBody>
      </Panel>

    </div>
  );
};

export default UpdateSupplierProducts;